import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import { personNameString } from '../Families/PersonName';
import {
  CommunityInfo,
  DeleteUploadedCommunityDocument,
  Permission,
  UploadedDocumentInfo,
} from '../GeneratedClient';
import { useCommunityCommand, useUserLookup } from '../Model/DirectoryModel';
import { useCommunityPermissions } from '../Model/SessionModel';
import {
  Delete as DeleteIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useBackdrop } from '../Hooks/useBackdrop';
import { downloadCommunityFile } from '../Model/FilesModel';
import { useRequiredSelectedLocationContext } from '../Model/Data';

interface CommunityDocumentsProps {
  communityInfo: CommunityInfo;
}
export function CommunityDocuments({ communityInfo }: CommunityDocumentsProps) {
  const permissions = useCommunityPermissions(communityInfo);
  const community = communityInfo.community!;

  const { organizationId, locationId } = useRequiredSelectedLocationContext();

  const userLookup = useUserLookup();
  const documents = (community.uploadedDocuments || []).map((document) => ({
    uploader: userLookup(document.userId),
    document: document,
  }));

  const deleteCommunityDocument = useCommunityCommand(
    (communityId, documentId: string) => {
      const command = new DeleteUploadedCommunityDocument();
      command.communityId = communityId;
      command.uploadedDocumentId = documentId;
      return command;
    }
  );

  const withBackdrop = useBackdrop();
  async function deleteDocument(document: UploadedDocumentInfo) {
    //TODO: Use the DeleteDocumentDialog approach - potentially making it reusable?
    if (
      window.confirm(
        'Are you sure you want to delete this document?\n\n' +
          document.uploadedFileName
      )
    ) {
      await withBackdrop(async () => {
        await deleteCommunityDocument(
          community.id!,
          document.uploadedDocumentId!
        );
      });
    }
  }

  async function downloadDocument(document: UploadedDocumentInfo) {
    await withBackdrop(async () => {
      await downloadCommunityFile(
        organizationId,
        locationId,
        community.id!,
        document.uploadedDocumentId!
      );
    });
  }

  const theme = useTheme();

  return (
    <List>
      {documents.map((doc) => (
        <ListItem
          key={doc.document.uploadedDocumentId}
          disablePadding
          secondaryAction={
            permissions(Permission.DeleteOrganizationDocuments) ? (
              <IconButton
                edge="end"
                aria-label="delete"
                color="primary"
                onClick={() => deleteDocument(doc.document)}
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }
        >
          {permissions(Permission.ReadOrganizationDocuments) ? (
            <ListItemButton
              disableGutters
              sx={{ paddingTop: 0, paddingBottom: 0 }}
              onClick={() => downloadDocument(doc.document)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <InsertDriveFileOutlinedIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={doc.document.uploadedFileName}
                slotProps={{
                  primary: { color: theme.palette.primary.main },
                }}
                secondary={`${format(doc.document.timestampUtc!, 'PPp')} — ${personNameString(doc.uploader)}`}
              ></ListItemText>
            </ListItemButton>
          ) : (
            <>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <InsertDriveFileOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={doc.document.uploadedFileName}
                secondary={`${format(doc.document.timestampUtc!, 'PPp')} — ${personNameString(doc.uploader)}`}
              ></ListItemText>
            </>
          )}
        </ListItem>
      ))}
    </List>
  );
}
